const MINUTES_IN_DAY = 24 * 60; // 1440 minutes

// -------------------------
// Fast Numeric Validation Helpers
// These functions operate on minutes since midnight for efficient comparisons.
// -------------------------

/**
 * Converts an HH:mm time string to minutes since midnight (0-1439).
 * Returns -1 for invalid time strings.
 */
export const timeToMinutes = (time: string): number => {
  if (!time || !time.includes(':')) return -1;
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return -1;
  return h * 60 + m;
};

/**
 * Converts minutes since midnight to HH:mm format.
 */
export const minutesToTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Converts 24-hour time (HH:mm) to 12-hour format with AM/PM.
 */
export const format12Hour = (time24: string): string => {
  if (!time24 || !time24.includes(':')) return time24;
  const [h, m] = time24.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return time24;
  
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

/**
 * Adds minutes to a time string and returns the next time slot.
 */
export const addMinutesToTime = (time24: string, minutesToAdd: number): string => {
  const minutes = timeToMinutes(time24);
  if (minutes === -1) return time24;
  const newMinutes = (minutes + minutesToAdd) % MINUTES_IN_DAY;
  return minutesToTime(newMinutes);
};

/**
 * Checks if a time slot (start and end) is fundamentally valid.
 * Conditions: both times exist, are parsable.
 *
 * NOTE: Same start/end time (e.g., '11:50' to '11:50') is now VALID and represents 24-hour operation.
 */
export const isValidTimeSlot = (start: string, end: string): boolean => {
  if (!start || !end) return false;
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  return s >= 0 && e >= 0; // Both must be valid minutes (same time is allowed for 24-hour operation)
};

/**
 * Determines if a time slot crosses midnight (i.e., it's an overnight shift).
 * This is true if the end time, when treated as minutes on the same calendar day, is
 * less than or equal to the start time (e.g., 22:00-06:00).
 */
export const isOvernightTime = (start: string, end: string): boolean => {
  // A slot can only be overnight if it's fundamentally valid
  if (!isValidTimeSlot(start, end)) return false;
  return timeToMinutes(end) <= timeToMinutes(start);
};

/**
 * Helper function to normalize a time slot into one or two 24-hour intervals.
 * This is crucial for correctly detecting overlaps with overnight periods.
 * An overnight slot like "22:00-06:00" is conceptually split into two intervals:
 * [22:00, 24:00) (today) and [00:00, 06:00) (tomorrow).
 *
 * Returns an array of [start_minutes, end_minutes] tuples.
 */
const getNormalizedIntervals = (startStr: string, endStr: string): Array<[number, number]> => {
  const s = timeToMinutes(startStr);
  const e = timeToMinutes(endStr);

  // If times are invalid, return an empty array of intervals
  if (s === -1 || e === -1) return [];

  if (s < e) {
    // Standard, non-overnight slot: treated as a single interval [start, end)
    return [[s, e]];
  } else {
    // Overnight slot: treated as two intervals that span across midnight
    // Part 1: From the start time to the end of the "current day" (23:59, represented as 1440 for exclusive end)
    // Part 2: From the start of the "next day" (00:00) to the end time
    return [[s, MINUTES_IN_DAY], [0, e]];
  }
};

/**
 * Checks for overlap between two time slots, robustly handling overnight periods.
 * It works by normalizing each slot into one or two 24-hour intervals, then
 * checking if any interval from slot1 overlaps with any interval from slot2.
 *
 * An overlap exists if: max(interval1_start, interval2_start) < min(interval1_end, interval2_end)
 *
 * @returns true if the two slots overlap, false otherwise.
 */
export const hasTimeOverlap = (
  slot1Start: string,
  slot1End: string,
  slot2Start: string,
  slot2End: string
): boolean => {
  // First, ensure both slots are fundamentally valid before attempting complex overlap checks
  if (!isValidTimeSlot(slot1Start, slot1End) || !isValidTimeSlot(slot2Start, slot2End)) {
    return false;
  }

  // Get the normalized intervals for both slots
  const intervals1 = getNormalizedIntervals(slot1Start, slot1End);
  const intervals2 = getNormalizedIntervals(slot2Start, slot2End);

  // Iterate through all possible pairs of normalized intervals
  // If any pair overlaps, then the original slots overlap
  for (const [s1_norm, e1_norm] of intervals1) {
    for (const [s2_norm, e2_norm] of intervals2) {
      // Standard overlap check for two intervals: (start1 < end2 AND start2 < end1)
      // Or, more compactly: Math.max(start1, start2) < Math.min(end1, end2)
      if (Math.max(s1_norm, s2_norm) < Math.min(e1_norm, e2_norm)) {
        return true; // Overlap detected
      }
    }
  }

  return false; // No overlap found after checking all combinations
};
